<template>
  <div
    class="conversations-list-wrap"
    :class="{
      hide: !showConversationList,
      'list--full-width': isOnExpandedLayout,
    }"
  >
    <slot />
    <div
      class="chat-list__top"
      :class="{ filter__applied: hasAppliedFiltersOrActiveFolders }"
    >
      <div class="box-inbox">
        <button
          class="scope-trigger"
          :title="$t('CHAT_LIST.TAB_HEADING')"
          @click="toggleSideMenu"
        >
          <svg class="scope-ic" viewBox="0 0 24 24">
            <path
              d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01"
            />
          </svg>
          <span class="scope-txt">
            <b class="text-truncate">{{ pageTitle }}</b>
            <small>{{ scopeSubtitle }}</small>
          </span>
          <svg class="scope-caret" viewBox="0 0 24 24">
            <path d="m6 9 6 6 6-6" />
          </svg>
        </button>

        <button
          class="scope-new"
          :title="$t('CONVERSATION.HEADER.NEW_MESSAGE') || 'Nova conversa'"
          @click="showModalNewConversation"
        >
          <svg viewBox="0 0 24 24">
            <path d="M12 20h9M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" />
          </svg>
        </button>
      </div>

      <!-- Alternância rápida de caixa de entrada no painel fixo (sem abrir o
           menu flutuante). Visível apenas com 2+ caixas. -->
      <div v-if="inboxes.length > 1" class="inbox-switch">
        <svg class="inbox-switch__ic" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M22 12h-6l-2 3h-4l-2-3H2" />
          <path
            d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11Z"
          />
        </svg>
        <select
          v-model="selectedInbox"
          class="inbox-switch__select"
          :title="$t('CHAT_LIST.INBOX_SWITCH.ALL')"
          @change="InboxSelectChange(selectedInbox)"
        >
          <option value="default">
            {{ $t('CHAT_LIST.INBOX_SWITCH.ALL') }}
          </option>
          <option v-for="inbox in inboxes" :key="inbox.id" :value="inbox.id">
            {{ inbox.name }}
          </option>
        </select>
        <svg class="inbox-switch__caret" viewBox="0 0 24 24" aria-hidden="true">
          <path d="m6 9 6 6 6-6" />
        </svg>
      </div>
    </div>

    <add-custom-views
      v-if="showAddFoldersModal"
      :custom-views-query="foldersQuery"
      :open-last-saved-item="openLastSavedItemInFolder"
      @close="onCloseAddFoldersModal"
    />

    <delete-custom-views
      v-if="showDeleteFoldersModal"
      :show-delete-popup.sync="showDeleteFoldersModal"
      :active-custom-view="activeFolder"
      :custom-views-id="foldersId"
      :open-last-item-after-delete="openLastItemAfterDeleteInFolder"
      @close="onCloseDeleteFoldersModal"
    />

    <div class="filters-box">
      <chat-type-tabs
        v-if="!hasAppliedFiltersOrActiveFolders"
        :items="assigneeTabItems"
        :active-tab="activeAssigneeTab"
        class="tab--chat-type"
        @chatTabChange="updateAssigneeTab"
      />
    </div>
    <div v-if="!hasAppliedFiltersOrActiveFolders" class="list-filters">
      <button class="chip-filter" @click="onToggleAdvanceFiltersModal">
        <svg class="chip-ic" viewBox="0 0 24 24">
          <path d="M22 3H2l8 9.5V19l4 2v-8.5L22 3Z" />
        </svg>
        {{ $t('CHAT_LIST.FILTERS') }}
      </button>
      <conversation-basic-filter @changeFilter="onBasicFilterChange" />
    </div>
    <div class="filter--actions">
      <div v-if="hasAppliedFilters && !hasActiveFolders">
        <woot-button
          v-tooltip.top-end="$t('FILTER.CUSTOM_VIEWS.ADD.SAVE_BUTTON')"
          size="tiny"
          variant="smooth"
          color-scheme="secondary"
          icon="save"
          @click="onClickOpenAddFoldersModal"
        />
        <woot-button
          v-tooltip.top-end="$t('FILTER.CLEAR_BUTTON_LABEL')"
          size="tiny"
          variant="smooth"
          color-scheme="alert"
          icon="dismiss-circle"
          @click="resetAndFetchData"
        />
      </div>
      <div v-if="hasActiveFolders">
        <woot-button
          v-tooltip.top-end="$t('FILTER.CUSTOM_VIEWS.EDIT.EDIT_BUTTON')"
          size="tiny"
          variant="smooth"
          color-scheme="secondary"
          icon="edit"
          @click="onToggleAdvanceFiltersModal"
        />
        <woot-button
          v-tooltip.top-end="$t('FILTER.CUSTOM_VIEWS.DELETE.DELETE_BUTTON')"
          size="tiny"
          variant="smooth"
          color-scheme="alert"
          icon="delete"
          @click="onClickOpenDeleteFoldersModal"
        />
      </div>
      <!-- <woot-button v-else v-tooltip.right="$t('FILTER.TOOLTIP_LABEL')" variant="smooth"
          color-scheme="secondary" icon="filter" size="tiny" @click="onToggleAdvanceFiltersModal" /> -->
    </div>

    <p v-if="!chatListLoading && !conversationList.length" class="content-box">
      {{ $t('CHAT_LIST.LIST.404') }}
    </p>
    <conversation-bulk-actions
      v-if="selectedConversations.length"
      :conversations="selectedConversations"
      :all-conversations-selected="allConversationsSelected"
      :selected-inboxes="uniqueInboxes"
      :show-open-action="allSelectedConversationsStatus('open')"
      :show-resolved-action="allSelectedConversationsStatus('resolved')"
      :show-snoozed-action="allSelectedConversationsStatus('snoozed')"
      @select-all-conversations="selectAllConversations"
      @assign-agent="onAssignAgent"
      @update-conversations="onUpdateConversations"
      @assign-labels="onAssignLabels"
      @assign-team="onAssignTeamsForBulk"
    />
    <div
      ref="activeConversation"
      class="conversations-list"
      :class="{ 'is-context-menu-open': isContextMenuOpen }"
    >
      <conversation-card
        v-for="chat in conversationList"
        :key="chat.uuid"
        :active-label="label"
        :team-id="teamId"
        :folders-id="foldersId"
        :chat="chat"
        :conversation-type="conversationType"
        :show-assignee="showAssigneeInConversationCard"
        :selected="isConversationSelected(chat.id)"
        @select-conversation="selectConversation"
        @de-select-conversation="deSelectConversation"
        @assign-agent="onAssignAgent"
        @assign-team="onAssignTeam"
        @assign-label="onAssignLabels"
        @update-conversation-status="toggleConversationStatus"
        @context-menu-toggle="onContextMenuToggle"
        @open-modal="openSnoozeModal"
        @mark-as-unread="markAsUnread"
        @assign-priority="assignPriority"
      />

      <div v-if="chatListLoading" class="text-center">
        <span class="spinner" />
      </div>

      <woot-button
        v-if="!hasCurrentPageEndReached && !chatListLoading"
        variant="clear"
        size="expanded"
        class="btn-primary"
        @click="loadMoreConversations"
      >
        {{ $t('CHAT_LIST.LOAD_MORE_CONVERSATIONS') }}
      </woot-button>

      <p
        v-if="showEndOfListMessage"
        class="text-center text-muted end-of-list-text"
      >
        {{ $t('CHAT_LIST.EOF') }}
      </p>
    </div>
    <woot-modal
      :show.sync="showAdvancedFilters"
      :on-close="closeAdvanceFiltersModal"
      size="medium"
    >
      <conversation-advanced-filter
        v-if="showAdvancedFilters"
        :initial-filter-types="advancedFilterTypes"
        :initial-applied-filters="appliedFilter"
        :active-folder-name="activeFolderName"
        :on-close="closeAdvanceFiltersModal"
        :is-folder-view="hasActiveFolders"
        @applyFilter="onApplyFilter"
        @updateFolder="onUpdateSavedFilter"
      />
    </woot-modal>

    <woot-modal
      :show.sync="toggleContactsModal"
      :on-close="hideContactsModal"
      size="modal-xl"
    >
      <div class="column content-box">
        <div class="row">
          <contacts-view
            is-picker
            @toggle-contacts-view="onToggleContactsModal"
          />
        </div>
      </div>
    </woot-modal>
    <woot-snoozed-modal
      v-if="showSnoozedModal"
      :show="showSnoozedModal"
      :scheduled-at="scheduledAt"
      @close="closeModal"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { BUS_EVENTS } from 'shared/constants/busEvents';

import languages from 'dashboard/components/widgets/conversation/advancedFilterItems/languages';
import wootConstants from 'dashboard/constants/globals';
import { generateValuesForEditCustomViews } from 'dashboard/helper/customViewsHelper';
import adminMixin from 'dashboard/mixins/isAdmin';
import AddCustomViews from 'dashboard/routes/dashboard/customviews/AddCustomViews';
import DeleteCustomViews from 'dashboard/routes/dashboard/customviews/DeleteCustomViews.vue';
import countries from 'shared/constants/countries';
import alertMixin from 'shared/mixins/alertMixin';
import eventListenerMixins from 'shared/mixins/eventListenerMixins';
import filterMixin from 'shared/mixins/filterMixin';
import WootSnoozedModal from '../components/widgets/conversation/WootSnoozedModal.vue';
import filterQueryGenerator from '../helper/filterQueryGenerator.js';
import conversationMixin from '../mixins/conversations';
import timeMixin from '../mixins/time';
import ChatTypeTabs from './widgets/ChatTypeTabs';
import advancedFilterTypes from './widgets/conversation/advancedFilterItems';
import ConversationAdvancedFilter from './widgets/conversation/ConversationAdvancedFilter';
import ConversationBasicFilter from './widgets/conversation/ConversationBasicFilter';
import ConversationBulkActions from './widgets/conversation/conversationBulkActions/Index.vue';
import ConversationCard from './widgets/conversation/ConversationCard';

import agentMixin from 'dashboard/mixins/agentMixin';
import uiSettingsMixin from 'dashboard/mixins/uiSettings';
import {
  hasPressedAltAndJKey,
  hasPressedAltAndKKey,
} from 'shared/helpers/KeyboardHelpers';
import { CONVERSATION_EVENTS } from '../helper/AnalyticsHelper/events';
import {
  closeConversation,
  conversationListPageURL,
  frontendURL,
} from '../helper/URLHelper';
import router from '../routes';
import ContactsView from '../routes/dashboard/contacts/components/ContactsView.vue';
import {
  isOnMentionsView,
  isOnUnattendedView,
} from '../store/modules/conversations/helpers/actionHelpers';
export default {
  components: {
    AddCustomViews,
    ChatTypeTabs,
    ConversationCard,
    ConversationAdvancedFilter,
    DeleteCustomViews,
    WootSnoozedModal,
    ConversationBulkActions,
    ConversationBasicFilter,
    ContactsView,
  },
  mixins: [
    timeMixin,
    conversationMixin,
    eventListenerMixins,
    alertMixin,
    filterMixin,
    adminMixin,
    uiSettingsMixin,
    agentMixin,
  ],
  props: {
    conversationInbox: {
      type: [String, Number],
      default: 0,
    },
    teamId: {
      type: [String, Number],
      default: 0,
    },
    label: {
      type: String,
      default: '',
    },
    conversationType: {
      type: String,
      default: '',
    },
    foldersId: {
      type: [String, Number],
      default: 0,
    },
    showConversationList: {
      default: true,
      type: Boolean,
    },
    isOnExpandedLayout: {
      default: false,
      type: Boolean,
    },
  },
  data() {
    return {
      showSnoozedModal: false,
      scheduledAt: false,
      readStatus: null,
      activeAssigneeTab: wootConstants.ASSIGNEE_TYPE.ME,
      activeStatus: wootConstants.STATUS_TYPE.OPEN,
      activeSortBy: wootConstants.SORT_BY_TYPE.LATEST,
      activeClassification: wootConstants.CLASSIFICATION_TYPE.ALL,
      showAdvancedFilters: false,
      advancedFilterTypes: advancedFilterTypes.map(filter => ({
        ...filter,
        attributeName: this.$t(`FILTER.ATTRIBUTES.${filter.attributeI18nKey}`),
      })),
      // chatsOnView is to store the chats that are currently visible on the screen,
      // which mirrors the conversationList.
      chatsOnView: [],
      foldersQuery: {},
      showAddFoldersModal: false,
      showDeleteFoldersModal: false,
      selectedConversations: [],
      selectedInboxes: [],
      isContextMenuOpen: false,
      appliedFilter: [],
      selectedInbox: 'default',
      toggleContactsModal: false,
      countChatsAll: null,
    };
  },
  computed: {
    ...mapGetters({
      currentChat: 'getSelectedChat',
      currentUser: 'getCurrentUser',
      chatLists: 'getAllConversations',
      accountId: 'getCurrentAccountId',
      getAccount: 'accounts/getAccount',
      mineChatsList: 'getMineChats',
      allChatList: 'getAllStatusChats',
      unAssignedChatsList: 'getUnAssignedChats',
      chatListLoading: 'getChatListLoadingStatus',
      currentUserID: 'getCurrentUserID',
      activeInbox: 'getSelectedInbox',
      conversationStats: 'conversationStats/getStats',
      appliedFilters: 'getAppliedConversationFilters',
      folders: 'customViews/getCustomViews',
      inboxes: 'inboxes/getInboxes',
      agentList: 'agents/getAgents',
      teamsList: 'teams/getTeams',
      inboxesList: 'inboxes/getInboxes',
      campaigns: 'campaigns/getAllCampaigns',
      labels: 'labels/getLabels',
      agentContact: 'contacts/getContactByAgentId',
      statusFilterConversationAndContact: 'getStatusFilter',
      readStatusFilter: 'getReadStatusFilter',
    }),
    hasAppliedFilters() {
      return this.appliedFilters.length !== 0;
    },
    hasActiveFolders() {
      return this.activeFolder && this.foldersId !== 0;
    },
    hasAppliedFiltersOrActiveFolders() {
      return this.hasAppliedFilters || this.hasActiveFolders;
    },
    savedFoldersValue() {
      if (this.hasActiveFolders) {
        const payload = this.activeFolder.query;
        this.fetchSavedFilteredConversations(payload);
      }
      return {};
    },
    showEndOfListMessage() {
      return (
        this.conversationList.length &&
        this.hasCurrentPageEndReached &&
        !this.chatListLoading
      );
    },
    currentUserDetails() {
      const { id, name } = this.currentUser;
      return {
        id,
        name,
      };
    },
    assigneeTabItems() {
      if (this.currentUser?.permissions?.see_only_my_conversations) {
        return [
          {
            key: 'me',
            name: this.$t('CHAT_LIST.ASSIGNEE_TYPE_TABS.me'),
            count: this.conversationStats.mineCount || 0,
          },
        ];
      }

      const ASSIGNEE_TYPE_TAB_KEYS = this.assigneeTabItemsFilter();
      let tabKey = {
        me: 'mineCount',
      };

      if (this.inbox.channel_type !== 'Channel::Internal') {
        tabKey.unassigned = 'unAssignedCount';
      }
      if (
        this.isAdministrator ||
        this.inbox.enable_view_assigned_conversations ||
        this.currentUser.permissions.see_all_conversations ||
        this.conversationType === 'mention' ||
        this.currentUser?.permissions?.team_chat_only ||
        (this.currentUser.permissions.see_conversations_team &&
          this.inbox.channel_type !== 'Channel::Internal')
      ) {
        tabKey.all = 'allCount';
      }
      // eslint-disable-next-line no-restricted-syntax
      for (const [key, value] of Object.entries(ASSIGNEE_TYPE_TAB_KEYS)) {
        if (key === this.inbox.id) {
          tabKey = value;
        }
      }
      return Object.keys(tabKey).map((key, i) => {
        let count = this.conversationStats[tabKey[key]] || 0;

        if (i === 2 && this.countChatsAll) {
          count = this.countChatsAll;
        }

        return {
          key,
          name: this.$t(`CHAT_LIST.ASSIGNEE_TYPE_TABS.${key}`),
          count,
        };
      });
    },
    showAssigneeInConversationCard() {
      return (
        this.hasAppliedFiltersOrActiveFolders ||
        this.activeAssigneeTab === wootConstants.ASSIGNEE_TYPE.ALL
      );
    },
    inbox() {
      return this.$store.getters['inboxes/getInbox'](this.activeInbox);
    },
    currentPage() {
      return this.$store.getters['conversationPage/getCurrentPageFilter'](
        this.activeAssigneeTab
      );
    },
    currentPageFilterKey() {
      return this.hasAppliedFiltersOrActiveFolders
        ? 'appliedFilters'
        : this.activeAssigneeTab;
    },
    currentFiltersPage() {
      return this.$store.getters['conversationPage/getCurrentPageFilter'](
        this.currentPageFilterKey
      );
    },
    hasCurrentPageEndReached() {
      return this.$store.getters['conversationPage/getHasEndReached'](
        this.currentPageFilterKey
      );
    },
    activeAssigneeTabCount() {
      const { activeAssigneeTab } = this;
      const count = this.assigneeTabItems?.find(
        item => item?.key === activeAssigneeTab
      ).count;
      return count;
    },
    conversationFilters() {
      return {
        inboxId: this.conversationInbox ? this.conversationInbox : undefined,
        assigneeType: this.activeAssigneeTab,
        status: this.activeStatus,
        sortBy: this.activeSortBy,
        classification: this.activeClassification,
        read_status: this.readStatus || this.readStatusFilter,
        contact_id: this.agentContact(this.currentUser.id)?.id,
        page: this.conversationListPagination,
        labels: this.label ? [this.label] : undefined,
        teamId: this.teamId || undefined,
        conversationType: this.conversationType || undefined,
        // folders: this.hasActiveFolders ? this.savedFoldersValue : undefined,
      };
    },
    conversationListPagination() {
      const conversationsPerPage = 25;
      const isNoFiltersOrFoldersAndChatListNotEmpty =
        !this.hasAppliedFiltersOrActiveFolders && this.chatsOnView.length !== 0;
      const isUnderPerPage =
        this.chatsOnView.length < conversationsPerPage &&
        this.activeAssigneeTabCount < conversationsPerPage &&
        this.activeAssigneeTabCount > this.chatsOnView.length;

      if (isNoFiltersOrFoldersAndChatListNotEmpty && isUnderPerPage) {
        return 1;
      }
      return this.currentPage + 1;
    },
    scopeSubtitle() {
      const account = this.getAccount(this.accountId);
      const name = account && account.name ? account.name : '';
      const openCount =
        this.conversationStats.allCount || this.conversationList.length || 0;
      const label = this.$tc('CHAT_LIST.SCOPE_OPEN_COUNT', openCount, {
        count: openCount,
      });
      return name ? `${name} · ${label}` : label;
    },
    pageTitle() {
      // if(this.inboxes.length > 1 && !this.inbox.name){
      //   return ;
      // }

      if (this.hasAppliedFilters) {
        if (this.inboxes.length < 2) {
          return this.$t('CHAT_LIST.TAB_HEADING');
        }
        return this.$t('CHAT_LIST.TAB_HEADING2');
      }
      if (this.inbox.name) {
        return this.inbox.name;
      }
      if (this.activeTeam.name) {
        return this.activeTeam.name;
      }
      if (this.label) {
        return `#${this.label}`;
      }
      if (this.conversationType === 'mention') {
        return this.$t('CHAT_LIST.MENTION_HEADING');
      }
      if (this.conversationType === 'participating') {
        return this.$t('CONVERSATION_PARTICIPANTS.SIDEBAR_MENU_TITLE');
      }
      if (this.conversationType === 'unattended') {
        return this.$t('CHAT_LIST.UNATTENDED_HEADING');
      }
      if (this.hasActiveFolders) {
        return this.activeFolder.name;
      }
      return this.$t('CHAT_LIST.TAB_HEADING');
    },
    contactConversations() {
      const currentId = this.currentUser?.id;
      const currentConversations = this.$store.getters[
        'contactConversations/getContactConversation'
      ](
        this.currentChat?.contact_id,
        currentId,
        this.isLimitedAgent,
        !this.toShowAllConversation()
      );
      return currentConversations;
    },
    conversationList() {
      let conversationList = [];

      const teamsMap = this.teamsList.reduce((map, team) => {
        map[team.id] = team;
        return map;
      }, {});

      const filters = this.conversationFilters;
      if (!this.hasAppliedFiltersOrActiveFolders) {
        if (this.activeAssigneeTab === 'me') {
          conversationList = [...this.mineChatsList(filters)];
        }

        if (this.activeAssigneeTab === 'unassigned') {
          conversationList = [
            ...this.unAssignedChatsList(
              filters,
              !this.toShowAllConversation(),
              this.isAgent
            ),
          ];
        }

        if (
          this.activeAssigneeTab !== 'me' &&
          this.activeAssigneeTab !== 'unassigned'
        ) {
          conversationList = [
            ...this.allChatList(filters, false, this.currentUserID),
          ];
        }
      }

      if (this.hasAppliedFiltersOrActiveFolders) {
        conversationList = [...this.chatLists];
      }

      let listAllChats = [];
      listAllChats = this.allChatList(
        filters,
        !this.toShowAllConversation(),
        this.currentUserID
      );

      if (
        this.currentUser.permissions.team_chat_only &&
        this.activeAssigneeTab === 'all' &&
        !this.currentUser.permissions.see_all_conversations
      ) {
        const listByTeam = listAllChats.filter(chat => {
          return (
            this.getMsgFromTeam(chat?.meta?.team?.id) || !chat.meta.assignee
          );
        });

        if (this.currentUser.permissions.team_chat_only) {
          // eslint-disable-next-line vue/no-side-effects-in-computed-properties
          this.countChatsAll = listByTeam.length;
        }

        conversationList = listByTeam;
      }
      if (!this.toShowAllInternalConversation()) {
        let filterConversationTeam = conversationList.filter(conversation => {
          const currentIDTeamAssignConversation = conversation?.meta?.team?.id;
          const assigneeID = conversation?.meta?.assignee?.id;
          const isPartOfMyTeam =
            teamsMap[currentIDTeamAssignConversation]?.is_member;
          const permissionsUser =
            this.currentUser?.type_role === 'administrator' ||
            this.currentUser?.permissions?.see_all_conversations;
          const conversationInbox = this.$store.getters['inboxes/getInbox'](
            conversation.inbox_id
          );
          const isInternalConversation =
            conversationInbox?.channel_type === 'Channel::Internal';
          switch (true) {
            case isInternalConversation:
              return true;
            case assigneeID === this.currentUserID:
              return true;
            case !assigneeID && isPartOfMyTeam === undefined:
              return true;
            case isPartOfMyTeam:
              return true;
            case permissionsUser:
              return true;
            default:
              return false;
          }
        });
        return filterConversationTeam;
      }

      if (this.activeAssigneeTab === 'unassigned' && this.activeInbox) {
        const sortedByDate = [...conversationList].sort(
          (a, b) => new Date(a.created_at) - new Date(b.created_at)
        );
        const ordemMap = new Map(
          sortedByDate.map((item, idx) => [item.id, idx + 1])
        );
        conversationList = conversationList.map(item => ({
          ...item,
          order: ordemMap.get(item.id),
        }));
      }

      if (!this.hasAppliedFiltersOrActiveFolders) {
        const readStatus = this.$store.getters.getReadStatusFilter;
        if (readStatus === 'read') {
          conversationList = conversationList.filter(c => c.unread_count === 0);
        } else if (readStatus === 'unread') {
          conversationList = conversationList.filter(c => c.unread_count > 0);
        }
      }

      return conversationList;
    },
    activeFolder() {
      if (this.foldersId) {
        const activeView = this.folders.filter(
          view => view.id === Number(this.foldersId)
        );
        const [firstValue] = activeView;
        return firstValue;
      }
      return undefined;
    },
    activeFolderName() {
      return this.activeFolder?.name;
    },
    activeTeam() {
      if (this.teamId) {
        return this.$store.getters['teams/getTeam'](this.teamId);
      }
      return {};
    },
    allConversationsSelected() {
      return (
        this.conversationList.length === this.selectedConversations.length &&
        this.conversationList.every(el =>
          this.selectedConversations.some(selected => selected.id === el.id)
        )
      );
    },
    uniqueInboxes() {
      return [...new Set(this.selectedInboxes)];
    },
  },
  watch: {
    activeAssigneeTab(newVal) {
      if (newVal === 'unassigned') {
        const filters = this.conversationFilters;
        this.conversationList = [
          ...this.unAssignedChatsList(
            filters,
            !this.toShowAllConversation(),
            this.isAgent
          ),
        ];
      }
      if (newVal === 'all') {
        const filters = this.conversationFilters;
        this.conversationList = [
          ...this.allChatList(
            filters,
            !this.toShowAllConversation(),
            this.currentUserID
          ),
        ];
      }
    },
    activeInbox: {
      handler(activeInbox) {
        if (!activeInbox) {
          this.selectedInbox = 'default';
        } else {
          this.selectedInbox = activeInbox;
        }
      },
      immediate: true,
    },
    $route: {
      handler() {
        this.hideContactsModal();
      },
      immediate: true,
    },
    statusFilterConversationAndContact: {
      handler(newValue) {
        if (newValue === 1) {
          this.onToggleAdvanceFiltersModal();
        }
      },
      immediate: true,
    },
    activeTeam() {
      this.resetAndFetchData();
    },
    conversationInbox() {
      this.resetAndFetchData();
    },
    label() {
      this.resetAndFetchData();
    },
    conversationType() {
      this.resetAndFetchData();
    },
    activeFolder() {
      this.resetAndFetchData();
    },
    chatLists() {
      this.chatsOnView = this.conversationList;
    },
  },
  mounted() {
    this.$store.dispatch('attributes/getGroupAttributes');
    this.$store.dispatch('setChatStatusFilter', this.activeStatus);
    this.$store.dispatch('setChatSortFilter', this.activeSortBy);
    this.$store.dispatch(
      'setChatClassificationFilter',
      this.activeClassification
    );
    this.resetAndFetchData();

    if (this.hasActiveFolders) {
      this.$store.dispatch('campaigns/get');
    }

    bus.$on('fetch_conversation_stats', () => {
      this.$store.dispatch('conversationStats/get', this.conversationFilters);
    });

    // document.addEventListener('click', this.handleGlobalClick);
  },

  // beforeDestroy() {
  //   document.removeEventListener('click', this.handleGlobalClick);
  // },

  methods: {
    // handleGlobalClick() {
    //   this.$store.dispatch('conversationStats/get', this.conversationFilters);
    // },
    onToggleContactsModal() {
      this.toggleContactsModal = !this.toggleContactsModal;
    },
    showModalNewConversation() {
      this.toggleContactsModal = true;
    },
    toggleSideMenu() {
      bus.$emit(BUS_EVENTS.TOGGLE_SIDEMENU);
    },
    hideContactsModal() {
      this.toggleContactsModal = false;
    },
    frontendURL,
    InboxSelectChange(id) {
      if (id !== 'default') {
        const path = frontendURL(`accounts/${this.accountId}/inbox/${id}`);
        router.push({ path });
        return;
      }

      const path = frontendURL(`accounts/${this.accountId}/conversation`);
      router.push({ path });
    },
    toggleConversationLayout() {
      const { LAYOUT_TYPES } = wootConstants;
      const {
        conversation_display_type: conversationDisplayType = LAYOUT_TYPES.CONDENSED,
      } = this.uiSettings;
      const newViewType =
        conversationDisplayType === LAYOUT_TYPES.CONDENSED
          ? LAYOUT_TYPES.EXPANDED
          : LAYOUT_TYPES.CONDENSED;
      this.updateUISettings({
        conversation_display_type: newViewType,
        previously_used_conversation_display_type: newViewType,
      });
    },
    assigneeTabItemsFilter() {
      let assigneeTypeTabKeys = {};

      this.inboxes.forEach(inbox => {
        if (inbox.channel_type !== 'Channel::Internal') {
          assigneeTypeTabKeys[inbox.id] = {
            me: 'mineCount',
            unassigned: 'unAssignedCount',
          };
        }
        if (this.toShowAllConversation()) {
          assigneeTypeTabKeys[inbox.id] = {
            ...assigneeTypeTabKeys[inbox.id],
            all: 'allCount',
          };
        }
        if (inbox.enable_view_assigned_conversations || this.isAdmin) {
          assigneeTypeTabKeys[inbox.id] = {
            ...assigneeTypeTabKeys[inbox.id],
            all: 'allCount',
          };
        }
      });

      return assigneeTypeTabKeys;
    },
    onApplyFilter(payload) {
      this.resetBulkActions();
      this.foldersQuery = filterQueryGenerator(payload);
      this.$store.dispatch('conversationPage/reset');
      this.$store.dispatch('emptyAllConversations');
      this.fetchFilteredConversations(payload);
      this.setStatusFilter(0);
    },
    setStatusFilter(status) {
      this.$store.dispatch('setStatusFilterConversationEndContact', { status });
    },
    onUpdateSavedFilter(payload, folderName) {
      const payloadData = {
        ...this.activeFolder,
        name: folderName,
        query: filterQueryGenerator(payload),
      };
      this.$store.dispatch('customViews/update', payloadData);
      this.closeAdvanceFiltersModal();
    },
    onClickOpenAddFoldersModal() {
      this.showAddFoldersModal = true;
    },
    onCloseAddFoldersModal() {
      this.showAddFoldersModal = false;
    },
    onClickOpenDeleteFoldersModal() {
      this.showDeleteFoldersModal = true;
    },
    onCloseDeleteFoldersModal() {
      this.showDeleteFoldersModal = false;
    },
    onToggleAdvanceFiltersModal() {
      if (!this.hasAppliedFilters && !this.hasActiveFolders) {
        this.initializeExistingFilterToModal();
      }
      if (this.hasActiveFolders) {
        this.initializeFolderToFilterModal(this.activeFolder);
      }
      this.showAdvancedFilters = true;
    },
    closeAdvanceFiltersModal() {
      this.showAdvancedFilters = false;
      this.appliedFilter = [];
      this.setStatusFilter(0);
    },
    setParamsForEditFolderModal() {
      // Here we are setting the params for edit folder modal to show the existing values.

      // For agent, team, inboxes,and campaigns we get only the id's from the query.
      // So we are mapping the id's to the actual values.

      // For labels we get the name of the label from the query.
      // If we delete the label from the label list then we will not be able to show the label name.

      // For custom attributes we get only attribute key.
      // So we are mapping it to find the input type of the attribute to show in the edit folder modal.
      const params = {
        agents: this.agentList,
        teams: this.teamsList,
        inboxes: this.inboxesList,
        labels: this.labels,
        campaigns: this.campaigns,
        contacts: this.$store.getters['contacts/getContacts'],
        languages: languages,
        countries: countries,
        filterTypes: advancedFilterTypes,
        allCustomAttributes: this.$store.getters[
          'attributes/getAttributesByModel'
        ]('conversation_attribute'),
      };
      return params;
    },
    toShowAllConversation() {
      return (
        (this.currentUser.type_role === 'administrator' ||
          this.currentUser?.permissions?.see_all_conversations ||
          this.currentUser?.permissions?.see_conversations_team) &&
        this.inbox.channel_type !== 'Channel::Internal'
      );
    },
    toShowAllInternalConversation() {
      return (
        this.currentUser.type_role === 'administrator' ||
        this.currentUser?.permissions?.see_all_conversations ||
        this.currentUser?.permissions?.see_conversations_team
      );
    },
    initializeFolderToFilterModal(activeFolder) {
      // Here we are setting the params for edit folder modal.
      //  To show the existing values. when we click on edit folder button.

      // Here we get the query from the active folder.
      // And we are mapping the query to the actual values.
      // To show in the edit folder modal by the help of generateValuesForEditCustomViews helper.
      const query = activeFolder?.query?.payload;
      if (!Array.isArray(query)) return;

      this.appliedFilter.push(
        ...query.map(filter => ({
          attribute_key: filter.attribute_key,
          attribute_model: filter.attribute_model,
          filter_operator: filter.filter_operator,
          values: Array.isArray(filter.values)
            ? generateValuesForEditCustomViews(
                filter,
                this.setParamsForEditFolderModal()
              )
            : [],
          query_operator: filter.query_operator,
          custom_attribute_type: filter.custom_attribute_type,
        }))
      );
    },
    getKeyboardListenerParams() {
      const allConversations = this.$refs.activeConversation.querySelectorAll(
        'div.conversations-list div.conversation'
      );
      const activeConversation = this.$refs.activeConversation.querySelector(
        'div.conversations-list div.conversation.active'
      );
      const activeConversationIndex = [...allConversations].indexOf(
        activeConversation
      );
      const lastConversationIndex = allConversations.length - 1;
      return {
        allConversations,
        activeConversation,
        activeConversationIndex,
        lastConversationIndex,
      };
    },
    handleKeyEvents(e) {
      if (hasPressedAltAndJKey(e)) {
        const {
          allConversations,
          activeConversationIndex,
        } = this.getKeyboardListenerParams();
        if (activeConversationIndex === -1) {
          allConversations[0].click();
        }
        if (activeConversationIndex >= 1) {
          allConversations[activeConversationIndex - 1].click();
        }
      }
      if (hasPressedAltAndKKey(e)) {
        const {
          allConversations,
          activeConversationIndex,
          lastConversationIndex,
        } = this.getKeyboardListenerParams();
        if (activeConversationIndex === -1) {
          allConversations[lastConversationIndex].click();
        } else if (activeConversationIndex < lastConversationIndex) {
          allConversations[activeConversationIndex + 1].click();
        }
      }
    },
    resetAndFetchData() {
      this.appliedFilter = [];
      this.resetBulkActions();
      this.$store.dispatch('conversationPage/reset');
      this.$store.dispatch('emptyAllConversations');
      this.$store.dispatch('clearConversationFilters');
      this.setStatusFilter(0);
      if (this.hasActiveFolders) {
        const payload = this.activeFolder.query;
        this.fetchSavedFilteredConversations(payload);
      }
      if (this.foldersId) {
        return;
      }
      this.fetchConversations();
    },
    fetchConversations() {
      this.$store
        .dispatch('fetchAllConversations', this.conversationFilters)
        .then(() => this.$emit('conversation-load'));
    },
    loadMoreConversations() {
      if (!this.hasAppliedFiltersOrActiveFolders) {
        this.fetchConversations();
      }
      if (this.hasActiveFolders) {
        const payload = this.activeFolder.query;
        this.fetchSavedFilteredConversations(payload);
      }
      if (this.hasAppliedFilters) {
        this.fetchFilteredConversations(this.appliedFilters);
      }
    },
    fetchFilteredConversations(payload) {
      let page = this.currentFiltersPage + 1;
      this.$store
        .dispatch('fetchFilteredConversations', {
          queryData: filterQueryGenerator(payload),
          page,
        })
        .then(() => this.$emit('conversation-load'));
      this.showAdvancedFilters = false;
    },
    fetchSavedFilteredConversations(payload) {
      let page = this.currentFiltersPage + 1;
      this.$store
        .dispatch('fetchFilteredConversations', {
          queryData: payload,
          page,
        })
        .then(() => this.$emit('conversation-load'));
    },
    updateAssigneeTab(selectedTab) {
      if (this.activeAssigneeTab !== selectedTab) {
        this.resetBulkActions();
        bus.$emit('clearSearchInput');
        this.activeAssigneeTab = selectedTab;
        if (!this.currentPage) {
          this.fetchConversations();
        }
      }
    },
    resetBulkActions() {
      this.selectedConversations = [];
      this.selectedInboxes = [];
    },
    onBasicFilterChange(value, type) {
      if (type === 'status') {
        this.activeStatus = value;
      } else if (type === 'sort') {
        this.activeSortBy = value;
      } else if (type == 'read_status') {
        this.readStatus = value;
      } else {
        this.activeClassification = value;
      }
      this.resetAndFetchData();
    },
    openLastSavedItemInFolder() {
      const lastItemOfFolder = this.folders[this.folders.length - 1];
      const lastItemId = lastItemOfFolder.id;
      this.$router.push({
        name: 'folder_conversations',
        params: { id: lastItemId },
      });
    },
    openLastItemAfterDeleteInFolder() {
      if (this.folders.length > 0) {
        this.openLastSavedItemInFolder();
      } else {
        this.$router.push({ name: 'home' });
        this.fetchConversations();
      }
    },
    isConversationSelected(id) {
      return this.selectedConversations.some(item => item.id === id);
    },
    selectConversation(conversationId, inboxId) {
      const conversation = this.conversationList.find(
        c => c.id === conversationId
      );
      this.selectedConversations.push({
        id: conversationId,
        isGroup: Boolean(conversation?.group?.jid),
      });
      this.selectedInboxes.push(inboxId);
    },
    deSelectConversation(conversationId, inboxId) {
      this.selectedConversations = this.selectedConversations.filter(
        item => item.id !== conversationId
      );
      this.selectedInboxes = this.selectedInboxes.filter(
        item => item !== inboxId
      );
    },
    selectAllConversations(check) {
      if (check) {
        this.selectedConversations = this.conversationList.map(item => ({
          id: item.id,
          isGroup: Boolean(item?.group?.jid),
        }));
        this.selectedInboxes = this.conversationList.map(item => item.inbox_id);
      } else {
        this.resetBulkActions();
      }
    },
    async onAssignAgent(agent, conversationId = null) {
      try {
        await this.$store.dispatch('bulkActions/process', {
          type: 'Conversation',
          ids: conversationId || this.selectedConversations?.map(c => c.id),
          fields: {
            assignee_id: agent.id,
          },
        });
        this.selectedConversations = [];
        if (conversationId) {
          this.showAlert(
            this.$t(
              'CONVERSATION.CARD_CONTEXT_MENU.API.AGENT_ASSIGNMENT.SUCCESFUL',
              {
                agentName: agent.name,
                conversationId,
              }
            )
          );

          closeConversation(
            this.accountId,
            router,
            this.currentUser?.id,
            agent?.id
          );
        } else {
          this.showAlert(this.$t('BULK_ACTION.ASSIGN_SUCCESFUL'));
        }
      } catch (err) {
        this.showAlert(this.$t('BULK_ACTION.ASSIGN_FAILED'));
      }
    },
    async assignPriority(priority, conversationId = null) {
      this.$store.dispatch('setCurrentChatPriority', {
        priority,
        conversationId,
      });
      this.$store
        .dispatch('assignPriority', { conversationId, priority })
        .then(() => {
          this.$track(CONVERSATION_EVENTS.CHANGE_PRIORITY, {
            newValue: priority,
            from: 'Context menu',
          });
          this.showAlert(
            this.$t('CONVERSATION.PRIORITY.CHANGE_PRIORITY.SUCCESSFUL', {
              priority,
              conversationId,
            })
          );
        });
    },
    async markAsUnread(conversationId) {
      try {
        await this.$store.dispatch('markMessagesUnread', {
          id: conversationId,
        });
        const {
          params: { accountId, inbox_id: inboxId, label, teamId },
          name,
        } = this.$route;
        let conversationType = '';
        if (isOnMentionsView({ route: { name } })) {
          conversationType = 'mention';
        } else if (isOnUnattendedView({ route: { name } })) {
          conversationType = 'unattended';
        }
        this.$router.push(
          conversationListPageURL({
            accountId,
            conversationType: conversationType,
            customViewId: this.foldersId,
            inboxId,
            label,
            teamId,
          })
        );
      } catch (error) {
        // Ignore error
      }
    },
    async onAssignTeam(team, conversationId = null) {
      try {
        await this.$store.dispatch('assignTeam', {
          conversationId,
          teamId: team.id,
        });
        this.showAlert(
          this.$t(
            'CONVERSATION.CARD_CONTEXT_MENU.API.TEAM_ASSIGNMENT.SUCCESFUL',
            {
              team: team.name,
              conversationId,
            }
          )
        );
      } catch (error) {
        this.showAlert(
          this.$t('CONVERSATION.CARD_CONTEXT_MENU.API.TEAM_ASSIGNMENT.FAILED')
        );
      }
    },
    async onAssignLabels(labels, conversationId = null) {
      try {
        await this.$store.dispatch('bulkActions/process', {
          type: 'Conversation',
          ids: conversationId || this.selectedConversations?.map(c => c.id),
          labels: {
            add: labels,
          },
        });
        this.selectedConversations = [];
        if (conversationId) {
          this.showAlert(
            this.$t(
              'CONVERSATION.CARD_CONTEXT_MENU.API.LABEL_ASSIGNMENT.SUCCESFUL',
              {
                labelName: labels[0],
                conversationId,
              }
            )
          );
        } else {
          this.showAlert(this.$t('BULK_ACTION.LABELS.ASSIGN_SUCCESFUL'));
        }
      } catch (err) {
        this.showAlert(this.$t('BULK_ACTION.LABELS.ASSIGN_FAILED'));
      }
    },
    async onAssignTeamsForBulk(team) {
      try {
        await this.$store.dispatch('bulkActions/process', {
          type: 'Conversation',
          ids: this.selectedConversations?.map(c => c.id),
          fields: {
            team_id: team.id,
          },
        });
        this.selectedConversations = [];
        this.showAlert(this.$t('BULK_ACTION.TEAMS.ASSIGN_SUCCESFUL'));
      } catch (err) {
        this.showAlert(this.$t('BULK_ACTION.TEAMS.ASSIGN_FAILED'));
      }
    },
    closeModal() {
      this.showSnoozedModal = false;
    },
    openSnoozeModal(value) {
      this.showSnoozedModal = value;
    },
    async onUpdateConversations(status) {
      try {
        await this.$store.dispatch('bulkActions/process', {
          type: 'Conversation',
          ids: this.selectedConversations?.map(c => c.id),
          fields: {
            status,
          },
        });
        this.selectedConversations = [];
        this.showAlert(this.$t('BULK_ACTION.UPDATE.UPDATE_SUCCESFUL'));
      } catch (err) {
        this.showAlert(this.$t('BULK_ACTION.UPDATE.UPDATE_FAILED'));
      }
    },
    toggleConversationStatus(conversationId, status, snoozedUntil) {
      const conversation =
        this.$store.getters.getConversationById(conversationId) ||
        this.currentChat;
      const contactId = conversation?.meta?.sender?.id || conversation?.contact_id;
      const contactConversations =
        this.$store.getters['contactConversations/getContactConversation'](
          contactId,
          this.currentUser?.id,
          this.isLimitedAgent,
          !this.toShowAllConversation()
        );
      const validateStatus = {
        activeInbox: this.activeInbox,
        contactConversations,
        currentId: this.currentUser?.id,
        contactId,
        messageSaved: this.$t('CONVERSATION.CHANGE_STATUS'),
        messageOpen: this.$t('CONTACT_PANEL.ALREADY_OPEN_CONVERSATION'),
        conversation,
      };
      this.$store
        .dispatch('toggleStatus', {
          conversationId,
          status,
          snoozedUntil,
          validateStatus,
        })
        .then(() => {
          if (status === 'resolved') {
            closeConversation(
              this.accountId,
              router,
              0,
              null,
              this.activeInbox
            );
          }
          this.isLoading = false;
        });
    },
    allSelectedConversationsStatus(status) {
      if (!this.selectedConversations.length) return false;
      return this.selectedConversations.every(item => {
        return (
          this.$store.getters.getConversationById(item.id).status === status
        );
      });
    },
    onContextMenuToggle(state) {
      this.isContextMenuOpen = state;
    },
    getMsgFromTeam(teamId) {
      return this.teamsList?.some(team => team.id === teamId && team.is_member);
    },
  },
};
</script>

<style scoped lang="scss">
@import '~dashboard/assets/scss/woot';

.filters-box {
  display: flex;
  flex-direction: column;
  // respiro generoso entre o gatilho de escopo e as pílulas
  margin-top: var(--space-two);
}

// Linha de Filtros + Ordenação abaixo das pílulas (igual ao .list-filters do protótipo)
.list-filters {
  display: flex;
  align-items: center;
  justify-content: space-between;
  // respiro generoso acima separando as pílulas dos filtros
  padding: var(--space-two) var(--space-normal) var(--space-slab);
}

.chip-filter {
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  font-size: var(--font-size-small);
  font-weight: var(--font-weight-medium);
  color: var(--s-600);
  // um pouco maior (5px 10px, igual ao .chip-filter do protótipo)
  padding: 0.5rem var(--space-one);
  border: 1px solid var(--color-border);
  border-radius: 20px;
  background: var(--color-surface);
  cursor: pointer;
  transition: border-color 0.15s ease-in-out, background 0.15s ease-in-out;

  &:hover {
    border-color: var(--s-300);
    background: var(--s-25);
  }

  .chip-ic {
    width: 15px;
    height: 15px;
    fill: none;
    stroke: currentColor;
    stroke-width: 2;
    stroke-linecap: round;
    stroke-linejoin: round;
  }
}

.box-inbox {
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: var(--space-small);
  // laterais já vêm do .chat-list__top (padding 0 16px); aqui só o respiro do topo
  padding: var(--space-normal) 0 0;
}

// Seletor rápido de caixa de entrada (painel fixo da lista de conversas)
.inbox-switch {
  position: relative;
  display: flex;
  align-items: center;
  gap: var(--space-smaller);
  margin-top: var(--space-small);
  padding: 0 var(--space-small);
  // largura cheia (igual à faixa das pílulas/abas), sem estourar pela borda
  width: 100%;
  box-sizing: border-box;
  height: 34px;
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius-normal);
  background: var(--color-surface);
  transition: border-color 0.15s ease-in-out;

  &:hover {
    border-color: var(--s-300);
  }

  .inbox-switch__ic {
    width: 15px;
    height: 15px;
    flex: none;
    fill: none;
    stroke: var(--color-woot-darker);
    stroke-width: 2;
    stroke-linecap: round;
    stroke-linejoin: round;
  }

  .inbox-switch__select {
    flex: 1;
    min-width: 0;
    height: 100%;
    // line-height = altura do conteúdo → centraliza o texto do select nativo,
    // alinhando com o ícone
    line-height: 32px;
    appearance: none;
    -webkit-appearance: none;
    border: 0;
    background: transparent;
    outline: none;
    margin: 0;
    padding: 0;
    font-size: var(--font-size-small);
    color: var(--s-800);
    cursor: pointer;
  }

  .inbox-switch__caret {
    width: 15px;
    height: 15px;
    flex: none;
    fill: none;
    stroke: var(--s-400);
    stroke-width: 2;
    stroke-linecap: round;
    stroke-linejoin: round;
    pointer-events: none;
  }
}

.chat-list__top {
  flex-direction: column;
}

// Gatilho de escopo (Todas as conversas + caixa · N abertas + caret) — abre o menu
.scope-trigger {
  display: flex;
  align-items: center;
  gap: var(--space-small);
  padding: var(--space-smaller) var(--space-small);
  // margem negativa cancela o padding próprio → ícone alinhado no grid de 16px
  margin-left: calc(-1 * var(--space-small));
  border-radius: var(--border-radius-normal);
  min-width: 0;
  cursor: pointer;
  transition: background 0.15s ease-in-out;

  &:hover {
    background: var(--s-50);
  }

  .scope-ic {
    width: 16px;
    height: 16px;
    flex: none;
    fill: none;
    stroke: #0a7581;
    stroke-width: 2;
    stroke-linecap: round;
    stroke-linejoin: round;
  }

  .scope-txt {
    text-align: left;
    min-width: 0;
    line-height: 1.2;

    b {
      display: block;
      font-size: var(--font-size-medium);
      font-weight: var(--font-weight-bold);
      color: var(--s-900);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    small {
      display: block;
      font-size: var(--font-size-mini);
      color: var(--s-400);
    }
  }

  .scope-caret {
    width: 15px;
    height: 15px;
    flex: none;
    fill: none;
    stroke: var(--s-400);
    stroke-width: 2;
    stroke-linecap: round;
    stroke-linejoin: round;
  }
}

.scope-new {
  flex: none;
  width: 34px;
  height: 34px;
  // margem negativa cancela o meio-vão do ícone no botão → alinhado no grid de 16px
  margin-right: calc(-1 * var(--space-small));
  border-radius: var(--border-radius-normal);
  display: grid;
  place-items: center;
  color: var(--s-600);
  cursor: pointer;
  transition: background 0.15s ease-in-out, color 0.15s ease-in-out;

  &:hover {
    background: var(--s-50);
    color: var(--color-woot);
  }

  svg {
    width: 18px;
    height: 18px;
    fill: none;
    stroke: currentColor;
    stroke-width: 2;
    stroke-linecap: round;
    stroke-linejoin: round;
  }
}

.spinner {
  margin-top: var(--space-normal);
  margin-bottom: var(--space-normal);
}

.conversations-list {
  // Prevent the list from scrolling if the submenu is opened
  &.is-context-menu-open {
    overflow: hidden !important;
  }
}

.conversations-list-wrap {
  flex-shrink: 0;
  margin-top: 10px;
  flex-basis: 34rem; // 340px — folga p/ as 3 pílulas nunca precisarem de scroll
  overflow: hidden;

  &.hide {
    display: none;
  }

  &.list--full-width {
    flex-basis: 100%;
  }

  .page-sub-title {
    font-size: var(--font-size-two);
  }
}

.filter--actions {
  display: flex;
  margin: 0 var(--space-normal);
  width: 100%;
  gap: var(--space-smaller);
}

.filter__applied {
  padding-bottom: var(--space-slab) !important;
  border-bottom: 1px solid var(--color-border);
}

.tab--chat-type {
  // laterais 16px, alinhadas ao cabeçalho e à linha de filtros (grid consistente)
  margin: 0 var(--space-normal);

  ::v-deep {
    .tabs {
      padding: 0;
    }
  }
}

.conversation--status-pill {
  background: var(--color-background);
  border-radius: var(--border-radius-small);
  color: var(--color-medium-gray);
  font-size: var(--font-size-micro);
  font-weight: var(--font-weight-medium);
  margin: var(--space-micro) var(--space-small) 0;
  padding: var(--space-smaller);
  text-transform: capitalize;
}

.chat-list__title {
  max-width: 85%;
}
</style>
